import type { MetaFunction } from '@remix-run/react';
import {
  Link,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from '@remix-run/react';
import { cn } from '@cardo/lib';
import { SocialLink } from '@cardo/ui';
import BrandedPageNav from '~/components/BrandedPageNav';
import { generateImgProps } from '~/lib/strapiImage';
import Layout, { LayoutPadding } from '~/components/layout/Layout';
import { json, type LoaderFunction } from '@remix-run/node';
import type { BrandedPage, StrapiImage } from '@cardo/types';
import NotFoundErrorBoundary from '~/components/error/NotFoundErrorBoundary';
import FullPageBackgroundLayout from '~/components/layout/FullPageBackgroundLayout';
import {
  generateMetaFromSeoComponent,
  generateOgUrlMetaDescriptor,
} from '~/lib/meta';
import { fetchStrapi, fetchStrapiNoLimit } from '~/lib/server/api.server';
import { subAffiliateCookie } from '~/lib/server/cookies.server';
import { envPrefixed, generateAppUrl } from '~/lib/utils';
import { useSubAffiliate } from '~/hooks/useSubAffiliate';
import { Hits, InstantSearch } from 'react-instantsearch';
import EmptyQueryBoundary from '~/components/search/EmptyQueryBoundary';
import FullCreditCardHit from '~/components/search/FullCreditCardHit';
import { searchClient } from '~/lib/search';
import { useEffect, useRef } from 'react';
import LayoutPaddingX from '~/components/layout/LayoutPaddingX';
import AdvertiserDisclosure from '~/components/AdvertiserDisclosure';
import logo from '~/images/logo-wide.svg';
import purpleEllipse from '~/images/purple-ellipse.webp';
import blueEllipse from '~/images/blue-ellipse.svg';
import background from '~/images/background-no-ellipses.webp';
import MainFooter from '~/components/layout/MainFooter';

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.data === 'DraftStateError')
      return (
        <Layout>
          <div className="flex flex-col space-y-4">
            <h1>Still in Draft</h1>
            <p>Sorry, this page is still in progress.</p>
          </div>
        </Layout>
      );

    return <NotFoundErrorBoundary />;
  }

  return (
    <FullPageBackgroundLayout>
      <h1 className="mb-8 text-3xl">Sorry, something went wrong.</h1>
      <p>
        Try going <Link to="/">Home</Link>
      </p>
    </FullPageBackgroundLayout>
  );
}

export function headers({ loaderHeaders }: { loaderHeaders: Headers }) {
  return { 'cache-control': loaderHeaders.get('cache-control') };
}

export const loader: LoaderFunction = async ({ params }) => {
  const { influencer: influencerUsername } = params;

  const slugRes = await fetchStrapi<BrandedPage[]>(
    '/branded-pages',
    {
      populate: 'influencer',
      filters: {
        slug: influencerUsername,
      },
      publicationState: 'preview',
    },
    {},
  );

  if (!slugRes.data || slugRes.data.length === 0) {
    throw new Response('Not Found', { status: 404 });
  }

  const influencerId = slugRes.data[0].attributes.influencer.data.id;
  const bankingPageEnabled =
    slugRes.data[0].attributes.influencer.data.attributes.bankingPageEnabled;
  const businessBankingPageEnabled =
    slugRes.data[0].attributes.influencer.data.attributes
      .businessBankingPageEnabled;

  if (!bankingPageEnabled) {
    throw new Response('Not Found', { status: 404 });
  }

  const disabled =
    slugRes.data[0].attributes.influencer.data.attributes.disabled;

  if (disabled) {
    throw new Response('Not Found', { status: 404 });
  }

  const res = await fetchStrapiNoLimit<BrandedPage[]>(
    '/branded-pages',
    {
      populate: {
        influencer: {
          populate: ['socialLinks', 'avatar'],
        },
      },
      publicationState: 'preview',
      filters: {
        influencer: influencerId,
      },
    },
    {},
  );

  if (!res.data || res.data.length === 0) {
    throw new Response('Not Found', { status: 404 });
  }

  const brandedPage = res.data.find(
    (page) => page.attributes.slug === influencerUsername,
  );

  if (brandedPage?.attributes.publishedAt === null) {
    throw new Response('DraftStateError', { status: 404 });
  }

  let headers = new Headers({
    'Cache-Control': 'max-age=1, stale-while-revalidate=31536000',
  });

  if (
    brandedPage &&
    brandedPage.attributes.influencer.data &&
    brandedPage.attributes.influencer.data.attributes.username
  ) {
    headers.append(
      'Set-Cookie',
      await subAffiliateCookie.serialize(
        brandedPage.attributes.influencer.data.attributes.username,
      ),
    );
  }

  return json(
    {
      brandedPage,
      brandedPages: res.data,
      bankingPageEnabled,
      businessBankingPageEnabled,
    },
    { headers },
  );
};

export const meta: MetaFunction = ({ data, location }) => {
  return [
    {
      title: 'Cardonomics - Banking',
    },
    {
      name: 'description',
      content: data?.brandedPage
        ? `${
            data?.brandedPage?.attributes.influencer.data.attributes
              .displayName ||
            data?.brandedPage?.attributes.influencer.data.attributes
          }'s top picks for high-yield savings accounts. Put your idle cash
          to work and earn interest.`
        : '',
    },
    {
      name: 'og:title',
      content: 'Cardonomics - Banking',
    },
    {
      name: 'og:description',
      content: data?.brandedPage
        ? `${
            data?.brandedPage?.attributes.influencer.data.attributes
              .displayName ||
            data?.brandedPage?.attributes.influencer.data.attributes
          }'s top picks for high-yield savings accounts. Put your idle cash
          to work and earn interest.`
        : '',
    },
    {
      name: 'og:type',
      content: 'website',
    },
    ...generateMetaFromSeoComponent(data?.brandedPage?.attributes.seo),
    generateOgUrlMetaDescriptor(location),
    {
      tagName: 'link',
      rel: 'canonical',
      href: generateAppUrl(location.pathname),
    },
  ];
};

export const topRightEllipseClassNamesNoHero =
  '-right-32 md:-right-16 -top-16 md:-top-40';
export const bottomLeftEllipseClassNamesNoHero =
  '-left-32 md:-left-16 -bottom-8 md:-bottom-12';

export default function BankingPage() {
  const loaderData = useLoaderData() as unknown as {
    brandedPage: BrandedPage;
    brandedPages: BrandedPage[];
    bankingPageEnabled: boolean;
    businessBankingPageEnabled: boolean;
  };

  const { brandedPage, brandedPages } = loaderData;

  useSubAffiliate(brandedPage.attributes.influencer.data?.id);

  const topPicksCategoryPages = brandedPages.filter(
    (page) => page.attributes.pageType === 'top-picks-category',
  );

  const userAvatar = brandedPage.attributes.influencer.data?.attributes
    .avatar as unknown as StrapiImage;

  useEffect(() => {
    if (!brandedPage.attributes.influencer.data?.id) return;

    const script = document.createElement('script');
    script.src = `//nextinsure.com/listingdisplay/loader/sh?ts=${Date.now()}`;
    script.onload = () => {
      const matchingConfiguration = {
        src: '692461',
        var1: '',
        var2: brandedPage.attributes.influencer.data?.id,
        var3: '',
        trn_id: '',
        rp: '17',
        inventorytype: 'valueprops-incontent',
        bankingrateproducttypes: 'MMA,Savings',
      };
      window.sh?.initialize(matchingConfiguration, 'qsWidgetContainer');
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [brandedPage.attributes.influencer.data?.id]);

  const mobileHeaderHeightRef = useRef<HTMLElement>(null);
  const mobileHeaderHeight = mobileHeaderHeightRef.current?.offsetHeight ?? 65;

  return (
    <div className="relative min-h-screen w-screen">
      <LayoutPaddingX className="shadow-md bg-white">
        <header className="hidden flex-col items-center py-5 md:flex">
          <nav className="flex items-center gap-5 w-full justify-between">
            <img src={logo} alt="cardonomics logo" />
            <AdvertiserDisclosure className="relative hidden md:flex" />
          </nav>
        </header>
      </LayoutPaddingX>
      <header
        className="fixed z-[1000] flex h-auto w-full md:hidden"
        ref={mobileHeaderHeightRef}
      >
        <nav className="w-full">
          <div className="flex w-full flex-col space-y-2 bg-white px-4 py-4 shadow">
            <div className="relative flex w-full items-center justify-center gap-3">
              <img src={logo} alt="cardonomics logo" />
            </div>
          </div>
        </nav>
      </header>
      <div
        className="min-h-[65px] md:hidden"
        style={{
          height: mobileHeaderHeight,
        }}
      ></div>
      <div className={'flex flex-grow flex-col relative pt-6 h-fit'}>
        <LayoutPaddingX className="absolute left-0 right-0 top-0 flex justify-center md:hidden px-8 py-4">
          <AdvertiserDisclosure className="relative" />
        </LayoutPaddingX>
        <div className="absolute left-0 right-0 top-0 z-[-1] h-full w-full overflow-hidden">
          <img
            src={background}
            alt="background"
            className="h-full w-full object-cover"
          />
          <img
            src={blueEllipse}
            alt="purple ellipse"
            className={cn('absolute', topRightEllipseClassNamesNoHero)}
          />
          <img
            src={purpleEllipse}
            alt="purple ellipse"
            className={cn('absolute', bottomLeftEllipseClassNamesNoHero)}
          />
        </div>
        <LayoutPaddingX className="h-full overflow-hidden">
          <section className="flex h-full flex-grow items-center justify-center">
            <div
              className={cn(
                'flex flex-col py-20',
                brandedPage.attributes.influencer.data.attributes.socialLinks
                  ?.length > 0
                  ? 'gap-2'
                  : 'gap-8',
                topPicksCategoryPages.length > 0 && 'mb-10',
              )}
            >
              <div className="flex flex-col items-center gap-4">
                <div
                  className={cn(
                    'flex h-44 w-44 items-center justify-center overflow-hidden rounded-full border shadow-md',
                    userAvatar.data && 'bg-white',
                    !userAvatar.data && 'bg-theme-blue-darkest',
                  )}
                >
                  {userAvatar.data && (
                    <img
                      {...generateImgProps(userAvatar)}
                      className="object-contain"
                      alt={`${brandedPage.attributes.influencer.data.attributes.username}'s avatar`}
                    />
                  )}
                </div>
              </div>
              {brandedPage.attributes.influencer.data.attributes.socialLinks
                ?.length > 0 && (
                <div className="mx-auto flex gap-3 pb-6 pt-4">
                  {brandedPage.attributes.influencer.data.attributes.socialLinks.map(
                    (socialLink) => (
                      <SocialLink key={socialLink.id} {...socialLink} />
                    ),
                  )}
                </div>
              )}
              <div className="flex flex-col gap-4">
                <h1 className="text-center">
                  {brandedPage.attributes.influencer.data.attributes
                    .displayName ||
                    brandedPage.attributes.influencer.data.attributes.username}
                  's Savings Picks
                </h1>
                <div
                  className={cn(
                    'max-w-11/12 prose-p:mb-2 text-center',
                    brandedPage.attributes.pageDescription.length > 200
                      ? 'sm:max-w-[900px]'
                      : 'sm:max-w-[600px]',
                  )}
                >
                  My top picks for high-yield savings accounts. Put your idle
                  cash to work and earn interest.
                </div>
              </div>
            </div>
          </section>
        </LayoutPaddingX>
      </div>
      <LayoutPadding>
        <main>
          {searchClient && (
            <InstantSearch
              searchClient={searchClient}
              indexName={envPrefixed('api::credit-card.credit-card')}
              future={{
                preserveSharedStateOnUnmount: true,
              }}
            >
              <div className="relative mx-auto w-full pt-20 xl:w-11/12">
                <div className="-mt-48 mb-16 w-full">
                  <BrandedPageNav
                    brandedPages={brandedPages}
                    bankingPageEnabled={loaderData.bankingPageEnabled}
                    businessBankingPageEnabled={
                      loaderData.businessBankingPageEnabled
                    }
                  />
                </div>
                <EmptyQueryBoundary
                  fallback={
                    <>
                      <div
                        className="md:min-h-auto mx-auto h-auto min-h-[300px] w-full max-w-6xl"
                        id="qsWidgetContainer"
                      ></div>
                    </>
                  }
                >
                  <Hits
                    hitComponent={FullCreditCardHit}
                    classNames={{
                      list: 'flex flex-col gap-8',
                    }}
                  />
                </EmptyQueryBoundary>
              </div>
            </InstantSearch>
          )}
        </main>
      </LayoutPadding>
      <MainFooter logoTo={null} />
    </div>
  );
}
